import React from 'react';
import { useIntl } from 'react-intl';
import { Rate, Empty } from 'antd';
import {
  StyledFarmRatings,
  StyledRatingCategory,
  StyledRatingComment,
} from './style';
import { Divider } from 'ui';
import { FarmRatingsProps } from './types';

const FarmRatings = ({ farmRatingsData }: FarmRatingsProps) => {
  const intl = useIntl();

  if (!farmRatingsData || farmRatingsData.total_ratings === 0) {
    return (
      <Empty description={intl.formatMessage({ id: 'rating.no_ratings' })} />
    );
  }

  const calculateRatingAverage = (
    rating: FarmRatingsProps['farmRatingsData']['ratings'][0]
  ) => {
    const ratings = [
      rating.product_quality_rating,
      rating.delivery_rating,
      rating.communication_rating,
      rating.payments_rating,
    ].filter((r): r is number => r !== null);

    if (ratings.length === 0) return 0;
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  };

  const ratingCategories = [
    { key: 'product_quality', value: farmRatingsData.product_quality_average },
    { key: 'delivery', value: farmRatingsData.delivery_average },
    { key: 'communication', value: farmRatingsData.communication_average },
    { key: 'payments', value: farmRatingsData.payments_average },
  ].filter((category) => category.value > 0);

  return (
    <StyledFarmRatings>
      <div className="rating-summary">
        {ratingCategories.map((category, index) => (
          <React.Fragment key={category.key}>
            {index > 0 && <Divider marginBottom={0} marginTop={0} />}
            <StyledRatingCategory>
              <span className="category-name">
                {intl.formatMessage({ id: `rating.${category.key}` })}
              </span>
              <Rate disabled allowHalf value={category.value} />
            </StyledRatingCategory>
          </React.Fragment>
        ))}
      </div>

      <div className="rating-comments">
        {farmRatingsData.ratings
          .filter((r) => r.comments)
          .map((rating) => {
            const averageRating = calculateRatingAverage(rating);
            const farmName = rating.farm?.brand || rating.farm?.name;

            return (
              <StyledRatingComment key={rating.id}>
                <div className="comment-header">
                  <strong>{farmName}</strong>
                  <div className="rating-wrapper">
                    <Rate disabled allowHalf value={averageRating} />
                  </div>
                </div>
                {rating.comments && (
                  <p className="comment-text">{rating.comments}</p>
                )}
              </StyledRatingComment>
            );
          })}
      </div>
    </StyledFarmRatings>
  );
};

export default FarmRatings;
